import { useEffect, useRef } from 'react';
import { FieldErrors, FieldValues, UseControllerProps } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { NormalizedLoad, TMSCarrier } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import {
  CarrierSearchableFields,
  carrierSearchHandler,
  mapCarriersToSelectOptions,
} from 'utils/loadInfoAndBuilding';

import { RHFDebounceSelect } from './RHFDebounceSelect';

type RHFCarrierSelectProps<TFieldValues extends FieldValues = NormalizedLoad> =
  Pick<
    UseControllerProps<TFieldValues>,
    'name' | 'rules' | 'control' | 'disabled'
  > & {
    label?: string;
    errors: FieldErrors;
    carriers: Maybe<TMSCarrier[]>;
    setCarriers: React.Dispatch<React.SetStateAction<Maybe<TMSCarrier[]>>>;
    isLoadingCarriers: boolean;
    handleRefreshCarriers: () => void;
    required?: boolean;
    placeholder?: string;
    tmsID?: number;
    valueRenderer?: (selectedOption: ValueType) => ValueType;
  };

export function RHFCarrierSelect<
  TFieldValues extends FieldValues = NormalizedLoad,
>({
  name,
  label = 'Name',
  disabled,
  control,
  errors,
  rules,
  carriers,
  setCarriers,
  isLoadingCarriers,
  handleRefreshCarriers,
  required = true,
  placeholder = 'Search by name, address, or DOT #',
  tmsID,
  valueRenderer,
}: RHFCarrierSelectProps<TFieldValues>) {
  const { tmsIntegrations } = useServiceFeatures();
  const effectiveTmsID = tmsID ?? tmsIntegrations?.[0]?.id;

  const originalCarriersRef = useRef<Maybe<TMSCarrier[]>>(null);

  useEffect(() => {
    if (carriers) {
      if (
        !originalCarriersRef.current ||
        carriers.length >= (originalCarriersRef.current.length || 0) ||
        !originalCarriersRef.current.length
      ) {
        originalCarriersRef.current = carriers;
      }
    }
  }, [carriers]);

  const handleCarrierSearch = async (
    field: CarrierSearchableFields,
    value: string
  ) => {
    if (!effectiveTmsID) {
      return [];
    }

    const carriersToSearch = originalCarriersRef.current || carriers;

    return carrierSearchHandler({
      tmsID: effectiveTmsID,
      carriers: carriersToSearch,
      setCarriers,
      field,
      value,
    });
  };

  return (
    <RHFDebounceSelect
      required={required}
      name={name}
      label={label}
      disabled={disabled}
      control={control}
      errors={errors}
      rules={rules}
      data={carriers}
      isLoading={isLoadingCarriers}
      refreshHandler={handleRefreshCarriers}
      fetchOptions={handleCarrierSearch}
      mapOptions={mapCarriersToSelectOptions}
      showSearchParamDropdown={false}
      searchFieldDefault='nameAddress'
      placeholder={placeholder}
      valueRenderer={valueRenderer}
    />
  );
}
